import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
  throwError
} from 'rxjs';
import { Router } from '@angular/router';
import { AuthTokenService } from '../Services/auth-token.service';
import { AuthService } from '../Services/auth.service';
import { ImpersonationFacade } from '../../../Facades/impersonation.facade';
import { AuthResponse, LaravelDefaultResponse } from '../Interfaces/response.interface';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../../../environments/endpoints';

let refreshRequest$: Observable<AuthResponse> | null = null;

// Pre-flighting outside /app would race AuthService's boot refresh on the public i18n fetch.
function targetsAuthenticatedApi(url: string): boolean {
  return url.startsWith(`${environment.api.host}/app/`);
}

function isRefreshRejected(error: unknown): boolean {
  return error instanceof HttpErrorResponse
    && (error.status === 401 || error.status === 403 || error.status === 422);
}

function withAccessToken(req: HttpRequest<unknown>, access_token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${access_token}`
    }
  });
}

function redirectToLogin(
  authTokenService: AuthTokenService,
  authService: AuthService,
  router: Router,
): void {
  authTokenService.setReturnUrl(router.url);
  // clearAuth(), not clearToken(): a stale isAuthenticatedSubject makes the guards bounce this redirect.
  authService.clearAuth();
  router.navigate(['/auth/login']);
}

function refreshAccessToken(
  http: HttpClient,
  authTokenService: AuthTokenService,
): Observable<AuthResponse> {
  if (!refreshRequest$) {
    const refresh_token = authTokenService.getRefreshToken();

    if (!refresh_token) {
      return throwError(() => new Error('No refresh token available.'));
    }

    refreshRequest$ = http.post<LaravelDefaultResponse<AuthResponse>>(
      `${environment.api.host}/${endpoints.auth.refresh}`,
      { refresh_token },
      {
        headers: {
          'skip-loading': 'true'
        }
      }
    ).pipe(
      map(response => response.data),
      tap(auth_data => authTokenService.setToken(auth_data)),
      finalize(() => {
        refreshRequest$ = null;
      }),
      shareReplay(1)
    );
  }

  return refreshRequest$;
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const httpBackend = inject(HttpBackend);
  const authTokenService = inject(AuthTokenService);
  const authService = inject(AuthService);
  const impersonationFacade = inject(ImpersonationFacade);
  const router = inject(Router);
  const http = new HttpClient(httpBackend);

  const handleUnauthorized = (
    request: HttpRequest<unknown>,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> => {
    const is_unauthorized = error.status === 401;
    const is_refresh_call = request.url.includes(endpoints.auth.refresh);
    const is_logout_call = request.url.includes(endpoints.auth.logout);
    const has_refresh_token = authTokenService.hasRefreshToken();

    if (!is_unauthorized || is_logout_call) {
      return throwError(() => error);
    }

    if (authTokenService.isImpersonated()) {
      impersonationFacade.endSession();
      return throwError(() => error);
    }

    if (is_refresh_call || !has_refresh_token) {
      redirectToLogin(authTokenService, authService, router);
      return throwError(() => error);
    }

    return refreshAccessToken(http, authTokenService).pipe(
      catchError((refresh_error: unknown) => {
        if (isRefreshRejected(refresh_error)) {
          redirectToLogin(authTokenService, authService, router);
        }

        return throwError(() => refresh_error);
      }),
      switchMap((auth_data) => next(withAccessToken(request, auth_data.access_token)))
    );
  };

  const send = (request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> =>
    next(request).pipe(
      catchError((error: HttpErrorResponse) => handleUnauthorized(request, error))
    );

  const access_token = authTokenService.getToken();
  const is_token_expired = authTokenService.isTokenExpired();

  const should_refresh_before_send =
    is_token_expired &&
    authTokenService.hasRefreshToken() &&
    !authTokenService.isImpersonated() &&
    targetsAuthenticatedApi(req.url);

  if (should_refresh_before_send) {
    return refreshAccessToken(http, authTokenService).pipe(
      map((auth_data) => auth_data.access_token),
      catchError((refresh_error: unknown) => {
        if (isRefreshRejected(refresh_error)) {
          redirectToLogin(authTokenService, authService, router);
        }

        // Sending the request anyway would 401 by construction and trigger a second refresh.
        return throwError(() => refresh_error);
      }),
      switchMap((fresh_token) => {
        if (authTokenService.isTokenExpired()) {
          redirectToLogin(authTokenService, authService, router);
          return throwError(() => new HttpErrorResponse({ status: 401, url: req.url }));
        }

        return next(withAccessToken(req, fresh_token));
      })
    );
  }

  return send(access_token && !is_token_expired ? withAccessToken(req, access_token) : req);
};
